/**
 * State for the local CMS backend.
 *
 * Owns the KB store, the current locale/selection, and the change history.
 * Persistence is localStorage only — see cms/store.ts and cms/history.ts.
 *
 * Scoped per team: kbId namespaces every key this hook touches, so each
 * team's knowledge base, history, and locale choice are kept apart. Passing a
 * different kbId (e.g. when the signed-in user switches teams) reloads the
 * store and history from that team's keys.
 */

import { describeEdit } from "../shared/useRecordDraft"
import { useStaging } from "../shared/useStaging"
import { useState, useEffect, useCallback, useRef } from "react"
import * as db from "./store"
import { toast } from "sonner"
import { type CmsAccess, checkArticlesWrite } from "../shared/permissions"
import { StoreSync, type SyncStatus } from "../shared/persistence/syncBridge"
import { makeArticlesAdapter } from "../shared/persistence/articlesAdapter"
import { containerId } from "../shared/persistence/types"
import { registerHistorySync, pullHistory } from "../shared/persistence/historySync"
import { type KBStore, type AnyNode, emptyStore, nodeTitle } from "./types"
import {
  type ChangeEntry, type ChangeAction, type NodeVersion, loadHistory, saveHistory,
  makeEntry, collectSubtree, revertEntry, clearHistory, versionsForNode, restoreVersion,
} from "./history"

const LOCALE_KEY_PREFIX = "kb-cms-locale"
const DEFAULT_KB = "default"

// The store's record buckets, which unpublished changes cover
const ARTICLE_BUCKETS = ["categories", "sections", "articles"] as const

// An article is on the public site once any language is published; categories and sections always are
const articleIsLive = (bucket: string, a: any) =>
  bucket !== "articles" || Object.values(a?.translations ?? {}).some((t: any) => (t?.state ?? a?.state) === "published")

function localeKeyFor(kbId: string) { return kbId === DEFAULT_KB ? LOCALE_KEY_PREFIX : `${LOCALE_KEY_PREFIX}:${kbId}` }

/** Identity recorded against each change, so the log shows who did what. */
export interface CMSAuthor {
  name?: string
  email?: string
  id?: string
  /** Model id, recorded when the agent is the one acting. */
  model?: string
}

export function useLocalCMS(author?: CMSAuthor, kbId: string = DEFAULT_KB, access: CmsAccess = "manage") {
  const syncRef = useRef<StoreSync<KBStore>>(null as any)
  if (!syncRef.current) syncRef.current = new StoreSync<KBStore>()
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(() => ({
    state: "idle", lastSyncedAt: null, error: null, pending: false,
  }))
  // The team whose rows have loaded; until then the store is a placeholder
  const [loadedFor, setLoadedFor] = useState("")

  const [store, setStoreState] = useState<KBStore>(db.emptyStoreFor)
  const [locale, setLocale] = useState<string>(() => localStorage.getItem(localeKeyFor(kbId)) || store.defaultLocale)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [history, setHistoryState] = useState<ChangeEntry[]>(() => loadHistory(kbId))

  // Mirror state in refs so tool closures read current values. An agent turn
  // makes many sequential tool calls without re-rendering, so a captured
  // snapshot would go stale after the first mutation.
  const storeRef = useRef(store)
  // Edits to existing records wait as unpublished changes; the live store is what the database and public sites have
  const staging = useStaging<KBStore>("articles", ARTICLE_BUCKETS, store, articleIsLive)
  const historyRef = useRef(history)
  // Author identity is read inside callbacks that shouldn't re-create on every
  // render, so keep it in a ref rather than a dependency.
  const authorRef = useRef(author)
  staging.setAuthor(() => ({ name: authorRef.current?.name, email: authorRef.current?.email }))
  const accessRef = useRef(access)
  accessRef.current = access
  useEffect(() => { authorRef.current = author }, [author])
  // kbId is read inside callbacks the same way — a ref avoids re-creating
  // every mutation callback whenever the team changes mid-render.
  const kbIdRef = useRef(kbId)

  useEffect(() => { storeRef.current = store }, [store])
  useEffect(() => { historyRef.current = history }, [history])
  useEffect(() => { localStorage.setItem(localeKeyFor(kbId), locale) }, [kbId, locale])

  // Team switch: reload the store, history, and locale from the new kbId's
  // own keys rather than continuing to show the previous team's data.
  useEffect(() => {
    if (kbIdRef.current === kbId) return
    kbIdRef.current = kbId
    // Cleared, not cached: the hydration effect below pulls this team's
    // real store from Postgres.
    const fresh = db.emptyStoreFor()
    staging.reset(fresh, kbId)
    storeRef.current = fresh
    setStoreState(fresh)
    const freshHistory = loadHistory(kbId)
    historyRef.current = freshHistory
    setHistoryState(freshHistory)
    setLocale(localStorage.getItem(localeKeyFor(kbId)) || fresh.defaultLocale)
    setSelectedId(null)
  }, [kbId])

  /** Single write path: updates React state, the ref, and localStorage together. */
  /*
   * Attach the database and hydrate from it. localStorage is a read-through
   * cache only; the database is authoritative.
   */
  useEffect(() => {
    const sync = syncRef.current
    // No team yet (still loading, or signed out) means there is nothing to
    // scope a query by -- not a fallback mode.
    if (!kbId) {
      sync.configure(null, "")
      registerHistorySync("articles", null)
      return
    }
    let cancelledHistory = false
    // The change log is team-scoped, so it keys off the team id directly
    // rather than the derived container id the entity tables use.
    registerHistorySync("articles", kbId)

    /*
     * Pull the change log too. Without this the log is write-only: entries
     * reach the database but every client reads its own localStorage copy,
     * so you cannot see -- or revert -- a change made from another browser.
     */
    void pullHistory("articles", kbId).then(remote => {
      if (cancelledHistory || !remote) return
      historyRef.current = remote as ChangeEntry[]
      setHistoryState(remote as ChangeEntry[])
    })
    // The container row's PK is derived, not the bare team id -- see containerId.
    const container = containerId("articles", kbId)
    sync.configure(makeArticlesAdapter(kbId), container)
    staging.reset(storeRef.current, kbId)

    let cancelled = false
    void (async () => {
      const result = await sync.pull()
      if (cancelled) return

      if (!result.ok) {
        /*
         * The load failed, so whatever is in memory came from the local cache
         * and may be stale. Block writes rather than letting a later edit
         * overwrite the database with it.
         */
        sync.blockWrites("Could not load the articles: " + result.error)
        return
      }

      // An empty team keeps what's in memory, which the first save seeds the database with
      const working = await staging.hydrate(result.store ?? storeRef.current)
      if (cancelled) return
      storeRef.current = working
      setStoreState(working)
      setLoadedFor(kbId)
      // result.empty means this team has no rows yet: keep what is in memory,
      // which the first save seeds the database with.
    })()
    return () => { cancelled = true; cancelledHistory = true }
  }, [kbId])

  useEffect(() => syncRef.current.subscribe(setSyncStatus), [])

  // A pending write should survive a tab close where possible. Fire-and-forget:
  // beforeunload cannot await, which is why the local copy stays authoritative.
  useEffect(() => {
    const onLeave = () => { void syncRef.current.flush() }
    window.addEventListener("beforeunload", onLeave)
    return () => {
      window.removeEventListener("beforeunload", onLeave)
      void syncRef.current.flush()
    }
  }, [])

  const setStore = useCallback((next: KBStore) => {
    const access = accessRef.current
    const staged = staging.split(storeRef.current, next, access === "manage")
    // View-only can't change anything; otherwise only what goes live is checked against the role
    const denied = access === "view"
      ? checkArticlesWrite(storeRef.current, next, access)
      : checkArticlesWrite(staging.live(), staged.live, access)
    if (denied) {
      toast.error(denied, { id: "cms-denied" }) // one toast, not one per keystroke
      throw new Error(denied)
    }
    staging.commit(staged)
    storeRef.current = next
    setStoreState(next)
    // Only live changes go to the content tables; unpublished ones are saved by staging
    if (staged.liveChanged) syncRef.current.push(staged.live)
  }, [])

  const pushHistory = useCallback((entry: ChangeEntry) => {
    const next = [...historyRef.current, entry]
    historyRef.current = next
    setHistoryState(next)
    saveHistory(next, kbIdRef.current)
  }, [])

  /**
   * Records a change. Call with the store as it was BEFORE the mutation so the
   * snapshot is the pre-change state — that's what revert restores.
   */
  const record = useCallback((args: {
    prevStore: KBStore
    action: ChangeAction
    actor: "agent" | "user"
    nodeId: string
    summary: string
    locale?: string
    /** Store after the change, for the `after` snapshot. Omit for deletes. */
    nextStore?: KBStore
  }) => {
    const { prevStore, nextStore, action, actor, nodeId, summary } = args
    const loc = args.locale ?? prevStore.defaultLocale

    // For a create there is no prior node, so read identity from the new store.
    const identitySource = action === "create" ? (nextStore ?? prevStore) : prevStore
    const node = db.getNode(identitySource, nodeId)
    if (!node) return

    const before: AnyNode[] =
      action === "create" ? []
      : action === "delete" ? collectSubtree(prevStore, nodeId)
      : [JSON.parse(JSON.stringify(node))]

    const afterNode = nextStore ? db.getNode(nextStore, nodeId) : null

    const who = authorRef.current
    pushHistory(makeEntry({
      action, actor, nodeId,
      authorName: who?.name,
      authorEmail: who?.email,
      authorId: who?.id,
      viaModel: actor === "agent" ? who?.model : undefined,
      nodeKind: node.kind,
      nodeTitle: nodeTitle(node, loc, identitySource.defaultLocale),
      path: db.pathOf(identitySource, nodeId, loc),
      locale: args.locale,
      summary,
      before: before.length ? { nodes: before } : undefined,
      after: afterNode ? { nodes: [JSON.parse(JSON.stringify(afterNode))] } : undefined,
    }))
  }, [pushHistory])

  // Managers publish: unpublished changes go live, each as one version
  const publishPending = useCallback((keys?: string[]) => {
    if (accessRef.current !== "manage") {
      toast.error("Only a manager can publish changes.", { id: "cms-denied" })
      return
    }
    // Only changes that are still waiting
    const chosen = (keys ?? staging.allKeys()).filter(k => staging.pendingEntity(k) !== undefined)
    if (!chosen.length) return
    const prevLive = staging.live()
    const plan = staging.publishPlan(chosen)
    syncRef.current.push(plan.live)
    staging.applyPlan(plan, chosen)
    storeRef.current = plan.working
    setStoreState(plan.working)
    for (const key of chosen) {
      const { bucket, id } = staging.splitKey(key)
      const before = (prevLive as any)[bucket]?.[id]
      const after = (plan.live as any)[bucket]?.[id]
      if (!before || !after) continue
      record({ prevStore: prevLive, nextStore: plan.live, action: "update", actor: "user", nodeId: id, summary: `Published: ${describeEdit(before, after).replace(/^Edited /, "")}` })
    }
  }, [record])

  // Throws unpublished changes away; the record goes back to what's live
  const discardPending = useCallback((keys: string[]) => {
    if (accessRef.current === "view" || !keys.length) return
    const prevWorking = storeRef.current
    const plan = staging.discardPlan(keys)
    staging.applyPlan(plan, keys)
    storeRef.current = plan.working
    setStoreState(plan.working)
    for (const key of keys) {
      const { id, bucket } = staging.splitKey(key)
      if (!(plan.working as any)[bucket]?.[id]) continue
      record({ prevStore: prevWorking, nextStore: plan.working, action: "update", actor: "user", nodeId: id, summary: "Discarded unpublished changes" })
    }
  }, [record])


  // ── UI mutations (actor: "user") ─────────────────────────────────────────

  const createCategory = useCallback((title = "New category") => {
    const prev = storeRef.current
    const r = db.createCategory(prev, title, locale)
    setStore(r.store)
    record({ prevStore: prev, nextStore: r.store, action: "create", actor: "user", nodeId: r.id, summary: `Created category "${title}"`, locale })
    setSelectedId(r.id)
  }, [locale, setStore, record])

  const createSection = useCallback((categoryId: string, parentSectionId: string | null, title = "New section") => {
    const prev = storeRef.current
    const r = db.createSection(prev, { title, categoryId, parentSectionId, locale })
    setStore(r.store)
    record({ prevStore: prev, nextStore: r.store, action: "create", actor: "user", nodeId: r.id, summary: `Created section "${title}"`, locale })
    setSelectedId(r.id)
  }, [locale, setStore, record])

  const createArticle = useCallback((sectionId: string, title = "Untitled article") => {
    const prev = storeRef.current
    const r = db.createArticle(prev, { title, body: "", sectionId, locale })
    setStore(r.store)
    record({ prevStore: prev, nextStore: r.store, action: "create", actor: "user", nodeId: r.id, summary: `Created article "${title}"`, locale })
    setSelectedId(r.id)
  }, [locale, setStore, record])

  const deleteNode = useCallback((id: string) => {
    const prev = storeRef.current
    const node = db.getNode(prev, id)
    if (!node) return
    const label = nodeTitle(node, locale, prev.defaultLocale)
    const r = db.deleteNode(prev, id)
    // Record before applying, so the snapshot sees the still-intact subtree.
    record({
      prevStore: prev, action: "delete", actor: "user", nodeId: id, locale,
      summary: `Deleted ${node.kind} "${label}" (${r.deleted.sections} section(s), ${r.deleted.articles} article(s) removed)`,
    })
    setStore(r.store)
    setSelectedId(cur => (cur === id ? null : cur))
  }, [locale, setStore, record])

  /** Commits an edit made in the node editor. */
  const commitEdit = useCallback((next: KBStore, nodeId?: string, summary?: string) => {
    const prev = storeRef.current
    setStore(next)
    if (nodeId) {
      record({
        prevStore: prev, nextStore: next, action: "update", actor: "user",
        nodeId, locale, summary: summary ?? "Edited content",
      })
    }
  }, [setStore, record, locale])

  /**
   * Removes a locale, refusing when it still holds content.
   *
   * Returns the reason on refusal rather than throwing: the caller is a
   * click handler, and an unhandled throw there surfaces as a blank console
   * error rather than something the user can read.
   */
  const removeLocale = useCallback((l: string): { ok: true; error?: undefined } | { ok: false; error: string } => {
    const blocker = db.localeRemovalError(storeRef.current, l)
    if (blocker) return { ok: false, error: blocker }
    setStore(db.removeLocale(storeRef.current, l))
    return { ok: true }
  }, [])

  /** Why `l` cannot be removed, or null when it can. For disabling the control. */
  const localeRemovalError = useCallback(
    (l: string) => db.localeRemovalError(storeRef.current, l), [])

  const addLocale = useCallback((l: string) => {
    setStore(db.ensureLocale(storeRef.current, l))
  }, [setStore])

  // Applies a batch of translations for one locale (used by the locale copy), one history entry per record
  const applyTranslations = useCallback((
    writes: Array<{ id: string; translation: any }>,
    locale: string,
    summary: string,
  ): { applied: number } => {
    const prev = storeRef.current
    const { store: next, applied } = db.setTranslationsBulk(
      prev,
      writes.map(w => ({ id: w.id, locale, translation: w.translation })),
    )
    if (!applied) return { applied: 0 }

    setStore(next)
    for (const w of writes) {
      record({
        prevStore: prev, nextStore: next,
        action: "translate", actor: "user",
        nodeId: w.id, locale, summary,
      })
    }
    return { applied }
  }, [setStore, record])

  const resetKB = useCallback(() => {
    setStore(emptyStore(storeRef.current.defaultLocale || "en-US"))
    setSelectedId(null)
  }, [setStore])

  // ── History ──────────────────────────────────────────────────────────────

  const revert = useCallback((entryId: string): { ok: true; description: string } | { ok: false; error: string } => {
    const entry = historyRef.current.find(e => e.id === entryId)
    if (!entry) return { ok: false, error: `No history entry with id ${entryId}` }

    const result = revertEntry(storeRef.current, entry)
    if ("error" in result) return { ok: false, error: result.error }

    setStore(result.store)

    // Mark the original reverted, and log the revert itself so the trail is complete.
    const marked = historyRef.current.map(e => e.id === entryId ? { ...e, reverted: true } : e)
    const who = authorRef.current
    const withRevert = [...marked, makeEntry({
      action: "revert",
      actor: "user",
      authorName: who?.name,
      authorEmail: who?.email,
      authorId: who?.id,
      nodeId: entry.nodeId,
      nodeKind: entry.nodeKind,
      nodeTitle: entry.nodeTitle,
      path: entry.path,
      locale: entry.locale,
      summary: result.description,
    })]
    historyRef.current = withRevert
    setHistoryState(withRevert)
    saveHistory(withRevert, kbIdRef.current)

    return { ok: true, description: result.description }
  }, [setStore])

  /** Version timeline for one node, newest first. */
  const versionsOf = useCallback((nodeId: string): NodeVersion[] =>
    versionsForNode(historyRef.current, nodeId), [])

  /**
   * Restores a node to one of its recorded versions. Logged as an update, so the
   * restore is itself revertable.
   */
  const restoreNodeVersion = useCallback((nodeId: string, entryId: string):
    { ok: true; description: string } | { ok: false; error: string } => {
    const version = versionsForNode(historyRef.current, nodeId)
      .find(v => v.entryId === entryId && !!v.node)
    if (!version?.node) return { ok: false, error: "That version has no saved snapshot to restore." }

    const prev = storeRef.current
    const next = restoreVersion(prev, version.node)
    setStore(next)
    record({
      prevStore: prev, nextStore: next, action: "update", actor: "user", nodeId,
      summary: `Restored to version from ${new Date(version.at).toLocaleString()} (${version.author})`,
    })
    return { ok: true, description: `Restored "${version.node.translations[Object.keys(version.node.translations)[0]]?.title ?? nodeId}" to its ${new Date(version.at).toLocaleString()} version` }
  }, [setStore, record])

  const wipeHistory = useCallback(() => {
    if (accessRef.current !== "manage") {
      toast.error("Only a manager can clear the change history.")
      return
    }
    clearHistory(kbIdRef.current)
    historyRef.current = []
    setHistoryState([])
  }, [])

  return {
    /** Database sync state; "offline" means no team selected yet. */
    syncStatus,
    /** Unpublished changes: what's waiting, and the store as it's live. */
    pending: {
      available: staging.available,
      keys: staging.pendingKeys,
      // Includes changes React hasn't rendered yet
      allKeys: staging.allKeys,
      liveStore: staging.liveStore,
      keyOf: staging.keyOf,
      liveEntity: staging.liveEntity,
      canPublish: access === "manage",
      publish: publishPending,
      discard: discardPending,
    },
    /** True once this team's rows have loaded from the database. */
    loaded: !!kbId && loadedFor === kbId,
    flushSync: () => syncRef.current.flush(),
    store, setStore,
    /** Always-current store reader for tool closures. See storeRef above. */
    getStore: () => storeRef.current,
    locale, setLocale,
    selectedId, setSelectedId,
    createCategory, createSection, createArticle, deleteNode, commitEdit, addLocale, removeLocale, localeRemovalError, resetKB,
    applyTranslations,

    history,
    /** Always-current history reader for tool closures. */
    getHistory: () => historyRef.current,
    record,
    revert,
    versionsOf,
    restoreNodeVersion,
    wipeHistory,
  }
}
